import { Empty, Tooltip } from 'antd';
import download from 'downloadjs';
import parser from 'html-react-parser';
import React, { useMemo, useState } from 'react';
import { AiOutlineCloudUpload, AiOutlineInfoCircle } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import Apis from '../../../../../api';
import useIsTeamMember from '../../../../../hooks/useIsTeamMember';
import { RootState } from '../../../../../store/rootReducer';
import BackDrop from '../../../../common/BackDrop';
import LoginActions from '../../../../login/redux/actions';
import { InvestorType } from '../../../../login/redux/types';
import MessageActions from '../../../../message/redux/actions';
import { AIQualificationStatus } from '../../Constant';
import DeleteAIDoc from './modal/Delete';

type Props = {
    investorType: InvestorType;
    selectedStatus: string;
    validate: boolean;
    setValidate: (f: boolean) => void;
    disabled: boolean;
};

const AIDocSection: React.FC<Props> = ({ investorType, selectedStatus, validate, setValidate, disabled }) => {
    const dispatch = useDispatch();

    const isTeamMember = useIsTeamMember();

    const investorUUID = useSelector((state: RootState) => state.user.uuid);
    const AIDocList = useSelector((state: RootState) => state.user.investor?.accreditation?.documents);

    const [loading, setLoading] = useState(false);

    const docList = useMemo(() => {
        const AIData = AIQualificationStatus.find((ai) => ai.type === investorType);
        if (AIData) {
            const docs = AIData.doc.find((d) => d.status === selectedStatus)?.list;
            return docs || [];
        }
        return [];
    }, [investorType, selectedStatus]);

    const title = useMemo(() => {
        const AIData = AIQualificationStatus.find((ai) => ai.type === investorType);
        if (AIData) {
            const title = AIData.doc.find((d) => d.status === selectedStatus)?.title;
            return title;
        }
        return undefined;
    }, [investorType, selectedStatus]);

    const onUpload = (file: File, type: string) => {
        if (investorUUID && type) {
            let formData = new FormData();
            formData.append('doc', file);
            formData.append('title', file.name);
            formData.append('type', type);
            setLoading(true);
            Apis.AIFileUpload(investorUUID, formData)
                .then(({ data }) => {
                    dispatch(LoginActions.updateInvestorInfo(data));
                    dispatch(MessageActions.showMessage({ text: 'Document uploaded successfully!', error: false }));
                    setValidate(false);
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ text: String(err), error: true }));
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const downloadAI = (d: {
        url: string;
        uuid: string;
        title?: string | undefined;
        type?: string | undefined;
        isDeleted: boolean;
    }) => {
        if (investorUUID && d.uuid) {
            setLoading(true);
            Apis.AIDocDownload(investorUUID, d.uuid)
                .then((res) => {
                    download(
                        new Blob([res.data], { type: res.headers['content-type'] }),
                        d.title || d.uuid,
                        res.headers['content-type'],
                    );
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    return (
        <>
            {title && <p className="WorkSans-Semibold text-[#20222B] text-sm">{title}</p>}
            {loading && <BackDrop />}
            {docList.map((doc, index) => {
                const isMandatory = AIDocList?.find(
                    (item) => item.type === doc.type && doc.mandatory && item.isDeleted === false,
                );
                const onlyOneFileMiss = AIDocList?.find(
                    (item) => item.type === doc.type && !doc.mandatory && item.isDeleted === false,
                );
                return (
                    <div className="flex flex-col mt-4" key={`${uuidv4()}_${index}`}>
                        <div className="bg-[#E4E2DA] px-3 py-2 flex justify-between">
                            <ul itemType="a" className="text-[#20222B] WorkSans">
                                <li className="py-1">
                                    {doc.title}{' '}
                                    {doc.mandatory ? <span className="text-red-500 font-bold"> *</span> : null}
                                    {doc.tooltip ? (
                                        <Tooltip title={parser(doc.tooltip)}>
                                            <AiOutlineInfoCircle
                                                size={'1.2rem'}
                                                color="#26AE60"
                                                className="inline ml-1 cursor-pointer"
                                            />
                                        </Tooltip>
                                    ) : null}
                                </li>
                            </ul>
                            {!disabled && !isTeamMember && (
                                <label htmlFor={doc.type} className="cursor-pointer">
                                    <AiOutlineCloudUpload size="1.4rem" color="#26AE60" />
                                </label>
                            )}
                            <input
                                id={doc.type}
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        const file: File = e.target.files[0];
                                        onUpload(file, e.target.id);
                                        e.target.value = '';
                                    }
                                }}
                            />
                        </div>
                        <div className="px-4 py-1 bg-white">
                            {AIDocList?.filter((d) => !d.isDeleted && d.type === doc.type).length === 0 && (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    className="m-0 mt-1"
                                    imageStyle={{ height: 25 }}
                                    description={<span className="text-sm">No Data</span>}
                                />
                            )}
                            {AIDocList?.filter((d) => !d.isDeleted && d.type === doc.type).map((d) => (
                                <div key={d.uuid} className="flex justify-between items-center py-2">
                                    <p
                                        className={`text-[#20222B] font-[WorkSans-Semibold] font-semibold text-sm ${
                                            !isTeamMember ? 'hover:underline cursor-pointer' : ''
                                        }`}
                                        onClick={() => {
                                            if (!isTeamMember) downloadAI(d);
                                        }}
                                    >
                                        {d.title || d.uuid}
                                    </p>
                                    {!disabled && !isTeamMember && <DeleteAIDoc uuid={d.uuid} />}
                                </div>
                            ))}
                        </div>

                        {validate && docList.length - 1 === index && !onlyOneFileMiss && (
                            <span className="common_error">Please upload at least one file.</span>
                        )}
                        {validate && doc.mandatory && !isMandatory && (
                            <span className="common_error">{`Please upload  ${doc.title} related file.`}</span>
                        )}
                    </div>
                );
            })}
        </>
    );
};

export default AIDocSection;
